import { useEffect, useState } from "react";
import { api } from "../lib/api";

const ksh = (n) => `KSh ${Math.round(n || 0).toLocaleString()}`;

/** The same facility in all 47 counties, ranked by surplus share. The priority counties are marked, not favoured. */
export const CountyComparison = ({ facility, funding }) => {
  const [data, setData] = useState(null);
  const [onlyPriority, setOnlyPriority] = useState(false);

  useEffect(() => {
    if (!facility) return;
    setData(null);
    api.get(`/facility-comparison/${facility}?funding=${funding || ""}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [facility, funding]);

  if (!data) return null;
  const rows = onlyPriority ? data.rows.filter((r) => r.priority) : data.rows;

  return (
    <section className="mt-20" data-testid="county-comparison">
      <p className="eyebrow">{data.min_population ? `${data.counties_shown} cities above a million, ranked` : "All 47 counties, ranked"}</p>
      <h2 className="display-2 mt-4 max-w-4xl">Where This Pays Best</h2>
      <p className="body-copy mt-5 max-w-3xl">
        {data.facility.name}, {data.funding.toLowerCase()} funded, modelled in {data.scope} and ranked by
        operating surplus as a share of build cost. Nothing is weighted towards the Priority 18 — they are simply marked so you can see how often
        they come out on top.
      </p>
      <p className="mt-6 max-w-3xl border-l-2 border-ochre bg-[#0b0b0b] p-5 text-sm leading-relaxed text-zinc-300" data-testid="comparison-reading">
        {data.reading}
      </p>

      <button
        type="button"
        onClick={() => setOnlyPriority((v) => !v)}
        data-testid="comparison-priority-toggle"
        className={`mono mt-7 border px-4 py-2.5 text-[0.55rem] uppercase tracking-[0.16em] transition-colors ${
          onlyPriority ? "border-ochre bg-ochre text-black" : "border-white/25 text-zinc-400 hover:border-ochre hover:text-ochre"
        }`}
      >
        {onlyPriority ? "Showing the Priority 18" : "Show only the Priority 18"}
      </button>

      <div className="mt-6 overflow-x-auto border border-zinc-900">
        <table className="w-full min-w-[900px] text-left" data-testid="comparison-table">
          <thead>
            <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
              {["#", "County", "Cost index", "Aged 15-34", "Build", "EBITDA", "Margin", "Yrs to match cost", "Surplus share"].map((h) => (
                <th key={h} className="mono px-4 py-3 text-[0.5rem] uppercase tracking-[0.16em] text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.county} className="border-b border-zinc-900" data-testid={`comparison-row-${r.county.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <td className="mono px-4 py-3 text-xs text-zinc-600">{String(r.rank).padStart(2, "0")}</td>
                <td className="px-4 py-3">
                  <span className="text-sm text-white">{r.county}</span>
                  {r.priority ? (
                    <span className="mono ml-2 border border-ochre/50 px-1.5 py-0.5 text-[0.45rem] uppercase tracking-[0.14em] text-ochre">
                      Priority 18
                    </span>
                  ) : null}
                </td>
                <td className="mono px-4 py-3 text-xs text-zinc-400">{r.cost_index}x</td>
                <td className="mono px-4 py-3 text-xs text-zinc-400">{(r.youth_15_34 || 0).toLocaleString()}</td>
                <td className="mono px-4 py-3 text-xs text-zinc-300">${(r.capex_usd || 0).toLocaleString()}</td>
                <td className="mono px-4 py-3 text-xs text-zinc-300">{ksh(r.ebitda_ksh)}</td>
                <td className="mono px-4 py-3 text-xs text-zinc-400">{r.ebitda_margin_pct}%</td>
                <td className="mono px-4 py-3 text-xs text-zinc-400">{r.payback_years ? `${r.payback_years} yrs` : "—"}</td>
                <td className="mono px-4 py-3 text-xs text-ochre">{r.roce_pct != null ? `${r.roce_pct}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};
